import { DestroyRef, inject, Injectable } from "@angular/core";
import { LogEntry } from "./log-entry.model";
import { LogStoreWriter } from "./log-store-writer";
import { RotationLimits } from "./log-rotation";
import { LOGS_DB_CONFIG } from "../storage/log-db.config";

/**
 * Buffer size at which `enqueue()` flushes immediately rather than waiting for `FLUSH_INTERVAL_MS`.
 */
const BATCH_SIZE_THRESHOLD = 20;
/** Upper bound on how long an entry can sit buffered before it's written to `gciins-logs-db`. */
const FLUSH_INTERVAL_MS = 5000;

/**
 * Owns the single `LogStoreWriter` that persists log entries to `gciins-logs-db`. `enqueue()` is
 * the hot path every `Logger` call goes through, so it is deliberately fire-and-forget: it only
 * pushes onto an in-memory buffer and (re)arms a flush timer, both O(1), and never awaits a write.
 *
 * A dedicated Web Worker previously sat in front of this same `LogStoreWriter`, with the writer
 * itself used only as a same-thread fallback for environments without real Workers (e.g. unit
 * tests). That indirection was removed: `IDBObjectStore` reads/writes are asynchronous by nature
 * and never block the main thread by themselves, so the Worker's only real benefit was offloading
 * `postMessage`'s structured-clone cost — not worth a second message-protocol contract and several
 * extra failure-handling paths. See Docs/app-logs.md#post-implementation-notes.
 *
 * @see Docs/app-logs.md#aggregator--rotation-design
 */
@Injectable({ providedIn: "root" })
export class LogAggregatorClient {
  private readonly destroyRef = inject(DestroyRef);
  private readonly writer = new LogStoreWriter(inject(LOGS_DB_CONFIG));
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("pagehide", this.handleFlushHint);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
      window.removeEventListener("pagehide", this.handleFlushHint);
    });
  }

  /** Applies the configured rotation limits; safe to call before or after the first `enqueue()`. */
  configure(limits: RotationLimits): void {
    this.writer.configure(limits);
  }

  /** Fire-and-forget: never awaited by call sites, so a log call is always O(1) on the main thread. */
  enqueue(entry: LogEntry): void {
    this.writer.enqueue(entry);
    if (this.writer.bufferSize >= BATCH_SIZE_THRESHOLD) {
      this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  /** Returns the most recent `limit` entries, newest first — used by the log viewer page. */
  query(limit: number): Promise<LogEntry[]> {
    return this.writer.flush().then(() => this.writer.query(limit));
  }

  /**
   * Drops any in-memory buffered entries and wipes the underlying IndexedDB store,
   * typically invoked from the log viewer's manual "Clear" action.
   *
   * Rejects if the underlying IndexedDB transaction fails — callers reporting the result
   * to the user (e.g. `AppLogs.clearLogs()`) should surface the rejection as a failure
   * instead of assuming success.
   */
  clear(): Promise<void> {
    this.clearFlushTimer();
    return this.writer.clear();
  }

  /**
   * Best-effort flush hints, mirroring `PwaUpdateService`'s existing visibility-driven poll
   * trigger: a backgrounded tab may never resume, so buffered-but-unflushed entries would
   * otherwise be lost.
   */
  private readonly handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.flush();
    }
  };

  private readonly handleFlushHint = (): void => {
    this.flush();
  };

  private flush(): void {
    this.clearFlushTimer();
    void this.writer.flush();
  }

  /** Batches writes via a macrotask, so IndexedDB work never runs inline with `enqueue()`. */
  private scheduleFlush(): void {
    if (this.flushTimer !== null) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.writer.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private clearFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}
